import { useMemo, useState } from 'react';
import {
  type ResourceSet,
  useResourceSets,
} from 'components/IAP/hooks/resourceSetHooks';
import ResourceSetEditDrawer from 'components/IAP/ResourceSetEditDrawer';
import { useAors } from 'hooks/useAors';
import { useCfaDemoEnabled } from 'utils/featureFlags';
import { useAorText } from 'utils/aorTerminology';
import { isHeleneConeResourceSet } from '../NotificationsTab/memphisThreatNotification';
import { useSitStatAorScope } from './SitStatAorScopeContext';
import { scopeIncludesAor, type SitStatAorScope } from './sitstat-aor-scope';
import { ResourceSetViewModal } from './ResourceSetViewModal';

export function filterSitStatAssets(
  resourceSets: ResourceSet[],
  scope: SitStatAorScope | null,
  cfaEnabled: boolean
): ResourceSet[] {
  return resourceSets.filter(
    (resourceSet) =>
      !resourceSet.archived &&
      (cfaEnabled || !isHeleneConeResourceSet(resourceSet)) &&
      scopeIncludesAor(scope, resourceSet.aor ? String(resourceSet.aor) : null)
  );
}

/**
 * SitStat Assets widget — a roster of the org's resource sets with their AOR
 * and status. An asset opens a read-only modal of every stored field, and Edit
 * there opens the resource set drawer.
 */
export function SitStatAssetsWidget() {
  const { data: resourceSets = [], isLoading } = useResourceSets();
  const { data: aors = [] } = useAors();
  const { scope } = useSitStatAorScope();
  const cfaEnabled = useCfaDemoEnabled();
  const aorText = useAorText();
  const [viewAsset, setViewAsset] = useState<ResourceSet | null>(null);
  const [editAsset, setEditAsset] = useState<ResourceSet | null>(null);

  const aorNameById = useMemo(
    () => new Map(aors.map((aor) => [String(aor.id), aor.name])),
    [aors]
  );
  const assets = useMemo(
    () =>
      filterSitStatAssets(resourceSets, scope, cfaEnabled).sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    [resourceSets, scope, cfaEnabled]
  );

  if (isLoading) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        Loading assets…
      </p>
    );
  }

  if (assets.length === 0) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        {scope ? aorText('No assets in this AOR.') : 'No assets.'}
      </p>
    );
  }

  return (
    <>
      <ul className="list-unstyled tw:m-0 tw:max-h-80 tw:w-full tw:list-none tw:!space-y-1.5 tw:overflow-y-auto tw:!p-0 tw:!pl-0 tw:pr-0.5">
        {assets.map((asset) => {
          const aorName = asset.aor ? aorNameById.get(String(asset.aor)) : null;
          const status = asset.asset_status || asset.status;
          return (
            <li key={asset.id}>
              <button
                type="button"
                onClick={() => setViewAsset(asset)}
                className="tw:w-full tw:cursor-pointer tw:rounded-md tw:border tw:border-border/60 tw:bg-transparent tw:px-2 tw:py-1.5 tw:text-left tw:text-foreground tw:hover:bg-accent/40"
              >
                <span className="tw:flex tw:items-center tw:justify-between tw:gap-2">
                  <span className="tw:min-w-0 tw:truncate tw:text-xs tw:font-medium">
                    {asset.name}
                  </span>
                  {status ? (
                    <span className="tw:shrink-0 tw:text-[11px] tw:text-muted-foreground">
                      {status}
                    </span>
                  ) : null}
                </span>
                <span className="tw:block tw:truncate tw:text-[11px] tw:text-muted-foreground">
                  {[asset.kind, asset.type, aorName]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <ResourceSetViewModal
        resourceSet={viewAsset}
        aorNameById={aorNameById}
        onClose={() => setViewAsset(null)}
        onEdit={(asset) => {
          setViewAsset(null);
          setEditAsset(asset);
        }}
      />
      {editAsset ? (
        <ResourceSetEditDrawer
          selectedEntity={editAsset}
          toggle={() => setEditAsset(null)}
        />
      ) : null}
    </>
  );
}
